import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";

import classNames from "classnames";

import { TryoutsFormProps } from "../types/tryouts";

import Loading from "../../../../../components/Loading";
import { selectFields } from "../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { displayFormAlert } from "../../../../../lib/utils";

const TryoutsForm = ({
    sending,
    methods,
    handleSubmit,
    setShow,
    tryout,
}: TryoutsFormProps): React.JSX.Element => {
    const fields = useAppSelector(selectFields);

    const {
        fields: dates,
        append,
        remove,
    } = useFieldArray({
        control: methods.control,
        name: "dates",
    });

    return (
        <Form
            id="clubhouseTeamTryoutForm"
            onSubmit={methods.handleSubmit(handleSubmit)}
            data-testid="clubhouseTeamTryoutForm"
        >
            <h2>Submit New Tryout</h2>
            {dates.map((date, index) => (
                <Row key={index}>
                    <Form.Group
                        controlId={`dates.${index}.date`}
                        as={Col}
                        md={3}
                        lg={3}
                        data-testid={`dates.${index}.date`}
                    >
                        <Form.Label id={`dates.${index}.date`}>
                            Tryout Date {dates.length > 1 && index + 1}
                        </Form.Label>
                        <Controller
                            control={methods.control}
                            name={`dates.${index}.date`}
                            render={({
                                field: { onChange, onBlur, value },
                            }) => (
                                <DatePicker
                                    ariaLabelledBy={`dates.${index}.date`}
                                    selected={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="MMMM d, yyyy"
                                    dropdownMode="select"
                                    className="form-control"
                                    minDate={new Date()}
                                />
                            )}
                        />
                    </Form.Group>
                    <Form.Group
                        controlId={`dates.${index}.startTime`}
                        as={Col}
                        lg={2}
                        md={2}
                        data-testid={`dates.${index}.startTime`}
                    >
                        <Form.Label id={`dates.${index}.startTime`}>
                            Start Time {dates.length > 1 && index + 1}
                        </Form.Label>
                        <Controller
                            control={methods.control}
                            name={`dates.${index}.startTime`}
                            rules={{
                                required: "Please specify a start time",
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                            }) => (
                                <DatePicker
                                    ariaLabelledBy={`dates.${index}.startTime`}
                                    selected={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    dateFormat="hh:mm aa"
                                    dropdownMode="select"
                                    className="form-control"
                                    minTime={new Date(0, 0, 0, 6, 0)}
                                    maxTime={new Date(0, 0, 0, 22, 0)}
                                />
                            )}
                        />
                        {methods.formState.errors.dates?.[index]?.startTime &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.dates?.[index]
                                    ?.startTime?.message as string,
                                settings: "mb-0",
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId={`dates.${index}.endTime`}
                        as={Col}
                        xl={2}
                        lg={3}
                        md={3}
                        data-testid={`dates.${index}.endTime`}
                    >
                        <Form.Label id={`dates.${index}.endTime`}>
                            End Time {dates.length > 1 && index + 1} (optional)
                        </Form.Label>
                        <Controller
                            control={methods.control}
                            name={`dates.${index}.endTime`}
                            render={({
                                field: { onChange, onBlur, value },
                            }) => (
                                <DatePicker
                                    ariaLabelledBy={`dates.${index}.endTime`}
                                    selected={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    isClearable
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    dateFormat="hh:mm aa"
                                    dropdownMode="select"
                                    className="form-control"
                                    minTime={
                                        methods.getValues(
                                            `dates.${index}.startTime`,
                                        ) || new Date(0, 0, 0, 6, 0)
                                    }
                                    maxTime={new Date(0, 0, 0, 22, 0)}
                                />
                            )}
                        />
                    </Form.Group>
                    <Col
                        xl={5}
                        lg={4}
                        md={3}
                        className={classNames("align-content-end")}
                    >
                        {index !== 0 && (
                            <Button
                                variant="danger"
                                onClick={() => {
                                    remove(index);
                                }}
                            >
                                Remove Date
                            </Button>
                        )}
                        {index === dates.length - 1 && (
                            <Button
                                className={classNames("mx-2")}
                                onClick={() => {
                                    append(
                                        {
                                            date: undefined,
                                            startTime: undefined,
                                            endTime: undefined,
                                        },
                                        { shouldFocus: false },
                                    );
                                }}
                            >
                                Add Additional Date
                            </Button>
                        )}
                    </Col>
                </Row>
            ))}
            <Row>
                <Col>
                    <Form.Group controlId="field" data-testid="field">
                        <Form.Label id="field">Location</Form.Label>
                        <Controller
                            name="field"
                            control={methods.control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    aria-labelledby="field"
                                    options={[...fields]
                                        .sort((a, b) =>
                                            a.name.localeCompare(b.name),
                                        )
                                        .map(f => {
                                            return {
                                                value: parseInt(
                                                    f.id.toString(),
                                                ),
                                                label: `${f.name} (${f.city})`,
                                            };
                                        })}
                                />
                            )}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="contactName">
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control {...methods.register("contactName")} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="contactEmail">
                        <Form.Label>Contact Email</Form.Label>
                        <Form.Control
                            type="email"
                            {...methods.register("contactEmail")}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="contactNumber">
                        <Form.Label>Contact Phone</Form.Label>
                        <Form.Control {...methods.register("contactNumber")} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="contactSite">
                        <Form.Label>Website</Form.Label>
                        <Form.Control {...methods.register("contactSite")} />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="description">
                        <Form.Label>Additional Details</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            {...methods.register("description")}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className={classNames("d-flex", "align-items-baseline")}>
                    <Button
                        variant="primary"
                        type="submit"
                        style={{ marginRight: "1em" }}
                        disabled={sending}
                    >
                        Submit Tryout Info
                    </Button>
                    {tryout && (
                        <Button
                            variant="danger"
                            type="button"
                            onClick={() => setShow(true)}
                            disabled={sending}
                        >
                            Delete Tryout Info
                        </Button>
                    )}
                    {sending === true && <Loading className="mx-3 w-auto" />}
                </Col>
            </Row>
        </Form>
    );
};

export default TryoutsForm;
